import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FloppyDisk, Clock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);
const hoursBetween = (start, end) => {
  const toM = (v) => { const [h, m] = (v || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const s = toM(start); const e = toM(end);
  if (!s || !e || e <= s) return [];
  const out = [];
  for (let m = Math.floor(s / 60) * 60; m + 60 <= Math.ceil(e / 60) * 60; m += 60) {
    const hh = Math.floor(m / 60);
    out.push(`${String(hh).padStart(2, "0")}:00`);
  }
  return out;
};

export default function Timesheets() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";

  const [date, setDate] = useState(iso(new Date()));
  const [schedules, setSchedules] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [operations, setOperations] = useState([]);
  const [drafts, setDrafts] = useState({}); // staff_id -> draft object
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    const [sch, ts, ops] = await Promise.all([
      api.get(`/schedules?date=${date}`),
      api.get(`/timesheets?date=${date}`),
      api.get("/operations"),
    ]);
    setSchedules(sch.data);
    setTimesheets(ts.data);
    setOperations(ops.data);
    // Seed drafts from existing timesheets, using each schedule's own shift window.
    const schByStaff = new Map(sch.data.map((s) => [s.staff_id, s]));
    const d = {};
    // First, seed empty drafts for every scheduled staff so the grids are preserved.
    for (const s of sch.data) {
      const hours = hoursBetween(s.shift_start, s.shift_end);
      const ops = (s.operations && s.operations.length > 0) ? s.operations : (s.operation_code ? [{ id: s.operation_id, code: s.operation_code, name: s.operation_name }] : []);
      d[s.staff_id] = {
        in_time: "", out_time: "", break_start: "", break_end: "", notes: "",
        attendance_status: "present",
        operation_override_ids: ops.map((o) => o.id),
        hourly: hours.map((h) => ({ hour: h, qty: "" })),
        daily_totals: ops.map((op) => ({ operation_id: op.id, operation_code: op.code, operation_name: op.name, qty: "" })),
      };
    }
    // Then overlay existing timesheet data.
    for (const t of ts.data) {
      const s = schByStaff.get(t.staff_id);
      const hours = s ? hoursBetween(s.shift_start, s.shift_end) : [];
      const ops = s ? ((s.operations && s.operations.length > 0) ? s.operations : (s.operation_code ? [{ id: s.operation_id, code: s.operation_code, name: s.operation_name }] : [])) : [];
      d[t.staff_id] = {
        in_time: t.in_time || "",
        out_time: t.out_time || "",
        break_start: t.break_start || "",
        break_end: t.break_end || "",
        notes: t.notes || "",
        attendance_status: t.attendance_status || "present",
        operation_override_ids: t.operation_override_ids || ops.map((o) => o.id),
        hourly: hours.map((h) => {
          const found = (t.hourly || []).find((x) => x.hour === h);
          return { hour: h, qty: found?.qty ?? "" };
        }),
        daily_totals: ops.map((op) => {
          const found = (t.daily_totals || []).find((x) => x.operation_id === op.id);
          return { operation_id: op.id, operation_code: op.code, operation_name: op.name, qty: found?.qty ?? "" };
        }),
      };
    }
    setDrafts(d);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const tsByStaff = useMemo(() => {
    const m = new Map();
    for (const t of timesheets) m.set(t.staff_id, t);
    return m;
  }, [timesheets]);

  const draftFor = (sid) => drafts[sid] || {
    in_time: "", out_time: "", break_start: "", break_end: "", notes: "",
    hourly: [], daily_totals: [],
  };
  const draftForSchedule = (sch) => {
    const existing = drafts[sch.staff_id];
    if (existing) return existing;
    const hours = hoursBetween(sch.shift_start, sch.shift_end);
    const ops = (sch.operations && sch.operations.length > 0) ? sch.operations : (sch.operation_code ? [{ id: sch.operation_id, code: sch.operation_code, name: sch.operation_name }] : []);
    return {
      in_time: "", out_time: "", break_start: "", break_end: "", notes: "",
      hourly: hours.map((h) => ({ hour: h, qty: "" })),
      daily_totals: ops.map((op) => ({ operation_id: op.id, operation_code: op.code, operation_name: op.name, qty: "" })),
    };
  };

  const updateDraft = (sid, patch) => {
    setDrafts((d) => ({ ...d, [sid]: { ...draftFor(sid), ...patch } }));
  };
  const updateHour = (sid, hour, qty) => {
    const cur = draftFor(sid);
    const hourly = cur.hourly.map((h) => h.hour === hour ? { ...h, qty } : h);
    updateDraft(sid, { hourly });
  };
  const updateDailyTotal = (sid, operation_id, qty) => {
    const cur = draftFor(sid);
    const daily_totals = (cur.daily_totals || []).map((e) => e.operation_id === operation_id ? { ...e, qty } : e);
    updateDraft(sid, { daily_totals });
  };

  const totalFor = (draft) => {
    const dt = (draft.daily_totals || []).reduce((a, e) => a + (parseFloat(e.qty) || 0), 0);
    if (dt > 0) return dt;
    return draft.hourly.reduce((a, h) => a + (parseFloat(h.qty) || 0), 0);
  };

  const save = async (sch) => {
    const d = draftFor(sch.staff_id);
    setSavingId(sch.staff_id);
    try {
      await api.post("/timesheets", {
        staff_id: sch.staff_id, date, schedule_id: sch.id,
        in_time: d.in_time || null,
        out_time: d.out_time || null,
        break_start: d.break_start || null,
        break_end: d.break_end || null,
        notes: d.notes || "",
        attendance_status: d.attendance_status || "present",
        operation_override_ids: d.operation_override_ids || [],
        hourly: d.hourly
          .filter((h) => h.qty !== "" && h.qty !== null)
          .map((h) => ({ hour: h.hour, qty: parseFloat(h.qty) || 0 })),
        daily_totals: (d.daily_totals || [])
          .filter((e) => e.qty !== "" && e.qty !== null)
          .map((e) => ({ operation_id: e.operation_id, qty: parseFloat(e.qty) || 0 })),
      });
      toast.success(`Saved · ${sch.staff_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingId(null); }
  };

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Timesheets</h1>
          <p className="text-xs text-zinc-600 mt-1">Enter what workers actually did each day — hours, breaks, hourly production.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="brut-label">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="brut-input" data-testid="ts-date" />
          <Link to={`/schedule/print?date=${date}`} className="brut-tag brut-tag-white text-xs underline">Print job cards</Link>
        </div>
      </div>

      {schedules.length === 0 ? (
        <div className="brut-card p-8 text-center text-zinc-500" data-testid="no-schedule">
          No one was scheduled on <b>{date}</b>. Assign staff on the <Link to="/schedule" className="underline font-bold">Schedule</Link> page first.
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map((sch) => {
            const existing = tsByStaff.get(sch.staff_id);
            const d = draftForSchedule(sch);
            const total = totalFor(d);
            return (
              <div key={sch.id} className="brut-card p-4" data-testid={`ts-card-${sch.staff_name}`}>
                <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <div className="font-display text-lg uppercase">{sch.staff_name} <span className="brut-tag brut-tag-yellow ml-1">{sch.staff_designation}</span></div>
                    <div className="text-xs text-zinc-600 font-mono-ui">{sch.operation_code} — {sch.operation_name} · {sch.shift_start}–{sch.shift_end}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {existing && <span className="brut-tag brut-tag-green inline-flex items-center gap-1"><CheckCircle size={10} weight="bold" /> Saved</span>}
                    {canEdit && (
                      <button
                        onClick={() => save(sch)}
                        disabled={savingId === sch.staff_id}
                        className="brut-btn brut-btn-primary px-3 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid={`save-ts-${sch.staff_name}`}
                      >
                        <FloppyDisk size={12} weight="bold" /> {savingId === sch.staff_id ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 brut-border p-3 bg-slate-50">
                  <div>
                    <label className="brut-label">Attendance</label>
                    <select
                      value={d.attendance_status || "present"}
                      onChange={(e) => updateDraft(sch.staff_id, { attendance_status: e.target.value })}
                      disabled={!canEdit}
                      className="brut-input mt-1"
                      data-testid={`attendance-${sch.staff_name}`}
                    >
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="half_day">Half-day</option>
                      <option value="sick">Sick</option>
                      <option value="leave">Leave</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="brut-label">Operations (override) <span className="text-[10px] text-zinc-500 font-normal">— check to change from schedule</span></label>
                    <div className="flex flex-wrap gap-2 mt-1 max-h-24 overflow-y-auto">
                      {operations.filter((o) => o.active).map((op) => {
                        const on = (d.operation_override_ids || []).includes(op.id);
                        return (
                          <label key={op.id} className={`inline-flex items-center gap-2 px-2 py-1 border-2 cursor-pointer text-xs ${on ? "border-blue-600 bg-blue-100" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                            <input
                              type="checkbox"
                              checked={on}
                              disabled={!canEdit}
                              onChange={() => {
                                const cur = d.operation_override_ids || [];
                                const next = on ? cur.filter((x) => x !== op.id) : [...cur, op.id];
                                // Sync daily_totals so the qty rows appear/disappear
                                const dt = next.map((id) => {
                                  const existing = (d.daily_totals || []).find((x) => x.operation_id === id);
                                  const full = operations.find((x) => x.id === id);
                                  return existing || { operation_id: id, operation_code: full?.code, operation_name: full?.name, qty: "" };
                                });
                                updateDraft(sch.staff_id, { operation_override_ids: next, daily_totals: dt });
                              }}
                            />
                            <span className="font-mono-ui font-bold" style={{ color: op.color || "#0f172a" }}>{op.code}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                  <TimeInput label="In Time" value={d.in_time} onChange={(v) => updateDraft(sch.staff_id, { in_time: v })} testid={`in-${sch.staff_name}`} disabled={!canEdit || d.attendance_status === "absent"} />
                  <TimeInput label="Out Time" value={d.out_time} onChange={(v) => updateDraft(sch.staff_id, { out_time: v })} testid={`out-${sch.staff_name}`} disabled={!canEdit || d.attendance_status === "absent"} />
                  <TimeInput label="Break Start" value={d.break_start} onChange={(v) => updateDraft(sch.staff_id, { break_start: v })} testid={`bs-${sch.staff_name}`} disabled={!canEdit || d.attendance_status === "absent"} />
                  <TimeInput label="Break End" value={d.break_end} onChange={(v) => updateDraft(sch.staff_id, { break_end: v })} testid={`be-${sch.staff_name}`} disabled={!canEdit || d.attendance_status === "absent"} />
                  <div>
                    <label className="brut-label inline-flex items-center gap-1"><Clock size={11} weight="bold" /> Total</label>
                    <div className="brut-input mt-1 font-mono-ui font-bold" data-testid={`total-${sch.staff_name}`}>{total}</div>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="brut-label mb-1">Day-End Totals <span className="text-[10px] text-zinc-500 font-normal">(one number per operation — preferred)</span></div>
                  {d.daily_totals && d.daily_totals.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {d.daily_totals.map((e) => (
                        <div key={e.operation_id} className="brut-border p-2 flex items-center gap-2 bg-blue-50">
                          <div className="min-w-0 flex-1">
                            <div className="font-mono-ui font-bold text-sm">{e.operation_code}</div>
                            <div className="text-[10px] text-zinc-600 truncate">{e.operation_name}</div>
                          </div>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={e.qty}
                            onChange={(ev) => updateDailyTotal(sch.staff_id, e.operation_id, ev.target.value)}
                            className="brut-input text-right font-mono-ui text-lg w-24"
                            disabled={!canEdit}
                            data-testid={`dt-${sch.staff_name}-${e.operation_code}`}
                            placeholder="qty"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-500">No operations on this schedule.</div>
                  )}
                </div>

                <details className="mt-3">
                  <summary className="brut-label cursor-pointer">Hourly Production (optional — collapse if not used)</summary>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-2">
                    {d.hourly.map((h) => (
                      <div key={h.hour}>
                        <div className="text-[10px] font-mono-ui text-zinc-600">{h.hour}</div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={h.qty}
                          onChange={(e) => updateHour(sch.staff_id, h.hour, e.target.value)}
                          className="brut-input mt-0.5 text-center font-mono-ui"
                          disabled={!canEdit}
                          data-testid={`h-${sch.staff_name}-${h.hour}`}
                        />
                      </div>
                    ))}
                  </div>
                </details>

                <div className="mt-3">
                  <label className="brut-label">Notes</label>
                  <textarea value={d.notes} onChange={(e) => updateDraft(sch.staff_id, { notes: e.target.value })} rows={1} className="brut-input mt-1" disabled={!canEdit} data-testid={`notes-${sch.staff_name}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TimeInput({ label, value, onChange, testid, disabled }) {
  return (
    <div>
      <label className="brut-label">{label}</label>
      <input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} className="brut-input mt-1" disabled={disabled} data-testid={testid} />
    </div>
  );
}
